// @flow
import * as React from 'react';
import styled from 'styled-components';
import Icon from 'components/Icon';
import Box from 'components/Box';
import Button from 'components/Button';
import Tooltip from 'components/Tooltip';
import Text, { BodySmall } from 'components/Text';
import { colors, breakpoints, gutterWidth, shadows } from '../theme';

const Tile = styled(({ expanded, noMask, children, ...props }) => (
  <Box {...props}>
    <Box position="relative">{children}</Box>
  </Box>
))`
  position: relative;
  padding: 16px ${gutterWidth / 2}px;
  @media (min-width: ${breakpoints[2]}) {
    padding: 16px 0;
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: calc(100% + 40px);
      height: calc(100% + 2px);
      background: white;
      border-radius: 4px;
      box-shadow: ${shadows.low};
      opacity: 0;
    }
    ${props =>
      props.expanded
        ? `
      &::before {
        opacity: 1;
      }
    `
        : `
    `}
  }
`;

const Filters = styled(Box)`
  @media (min-width: ${breakpoints[2]}) {
    padding: 0 20px 17px;
    margin: 0 -20px -17px;
    ${props =>
      !props.$noMask &&
      `
    overflow-x: hidden;
    overflow-y: auto;
    &::before,
    &::after {
      content: '';
      position: absolute;
      left: 0;
      z-index: 1;
      width: 100%;
      height: 20px;
    }
    &::before {
      top: 0;
      background: linear-gradient(to bottom,
        rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0));
    }
    &::after {
      bottom: 0;
      background: linear-gradient(
        to top,
        rgba(255, 255, 255, 0.9),
        rgba(255, 255, 255, 0)
      );
    }`}
  }
`;

const SearchFilterSection = ({
  title,
  header,
  children,
  noToggle,
  count,
  toggleExpanded,
  expanded,
  noMask,
  hasOptionFilter,
  tooltip,
  ...props
}: {
  title: string,
  header?: React.Node,
  children: any,
  noToggle?: boolean,
  count?: number,
  toggleExpanded?: Function,
  expanded?: boolean,
  noMask?: boolean,
  hasOptionFilter?: boolean,
  tooltip?: React.Node
}) => (
  <Box
    mt="-1px"
    borderBottom={
      noToggle
        ? undefined
        : [
            `1px solid ${colors.aluminum}`,
            null,
            null,
            expanded ? '1px solid transparent' : null
          ]
    }
    borderTop={[
      `1px solid ${colors.aluminum}`,
      null,
      null,
      expanded ? '1px solid transparent' : null
    ]}
    className={expanded ? 'is-expanded' : undefined}
    {...props}
  >
    <Tile expanded={expanded} noMask={noMask}>
      <Button
        onClick={noToggle ? undefined : toggleExpanded}
        width="100%"
        tabIndex={noToggle ? '-1' : undefined}
      >
        <BodySmall m={0} data-qa-id="section-heading">
          <Box
            display="flex"
            alignItems="center"
            color={expanded && colors.waterBlue}
          >
            <Box display="flex" alignItems="center">
              {title}
              {tooltip && (
                <Box ml="6px" onClick={e => e.stopPropagation()}>
                  <Tooltip component="div">{tooltip}</Tooltip>
                </Box>
              )}
            </Box>
            {noToggle || (
              <Box ml="auto">
                {typeof count === 'number' && count > 0 && (
                  <Box display="inline-block" mr={24}>
                    <Text color={colors.waterBlue}>{count}</Text>
                  </Box>
                )}
                <Icon
                  name="chevron"
                  verticalAlign="middle"
                  fill={colors.waterBlue}
                  stroke={colors.waterBlue}
                  direction={expanded ? 'down' : 'up'}
                  width={9}
                />
              </Box>
            )}
          </Box>
        </BodySmall>
      </Button>
      <Box
        display={expanded || noToggle ? 'block' : 'none'}
        mt={noToggle ? 0 : 16}
        borderTop={
          noMask || noToggle ? undefined : `1px solid ${colors.aluminum}`
        }
      >
        {header}
        <Box position="relative">
          <Filters
            data-qa-id="search-filter-section-filters"
            $noMask={noMask || noToggle}
            maxHeight={[
              null,
              null,
              null,
              noMask || noToggle ? undefined : hasOptionFilter ? 324 : 302
            ]}
          >
            {children}
          </Filters>
        </Box>
      </Box>
    </Tile>
  </Box>
);

export default SearchFilterSection;
